import React, { useMemo } from 'react';
import { ColumnPinningState } from '@tanstack/react-table';

import {
  DataTable,
  DataTableColumnDef,
  DefaultCkTableOptions,
  TableDensity,
  useDataTable,
  UseDataTableInputOptions,
  usePersistentState,
} from '@crossnokaye/ux-common';

import { AlarmActivation } from '@repo/sdk/models/components';

import { TableTooltipIsland } from '@/components/data-table';
import { MISSING_VALUE } from '@/constants';
import {
  AcknowledgedAtColumnDef,
  AcknowledgedByColumnDef,
  ActionColumnDef,
  ActiveForColumnDef,
  DateColumnDef,
  DeviceNameColumnDef,
  DeviceTypeColumnDef,
  IssueColumnDef,
  ResolvedAtColumnDef,
  SeverityColumnDef,
  StatusColumnDef,
} from '@/features/alarms/alarm-tables/columns';
import {
  COLUMN_ID_ACTION,
  COLUMN_ID_DATE,
  COLUMN_ID_DEVICE_NAME,
  COLUMN_ID_SEVERITY,
} from '@/features/alarms/alarm-tables/constants';
import {
  getElapsedTime,
  getSnapshotString,
  getStatus,
} from '@/features/alarms/alarm-tables/helpers';
import { ActivationsDataTableColumnMeta, NewOptions } from '@/features/alarms/alarm-tables/types';
import {
  AlarmsTableProps,
  AlarmTableData,
  DataTableCache,
} from '@/features/alarms/alarm-tables/types';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import {
  DeviceTypeDetectionVariant,
  useDeviceTypeDetection,
} from '@/ux-common/hooks/use-device-type-detection/use-device-type-detection';

/**
 * AcknowledgedDataTableColumnDef is the definition for the acknowledged data table.
 * It's columns have their own callback defined in Meta, we need to define with the table
 * instead of putting these properties on a system wide columnDef interface.
 */
export type AcknowledgedDataTableColumnDef<TData, TValue> = DataTableColumnDef<TData, TValue> & {
  meta?: ActivationsDataTableColumnMeta<TData, TValue>;
};

// Custom options for the acknowledged alarms table.
const options: UseDataTableInputOptions<AlarmTableData> & NewOptions = {
  ...DefaultCkTableOptions,
  cache: {
    columnSizing: true,
    sort: true,
  },
  columnPinningState: {
    left: [COLUMN_ID_SEVERITY, COLUMN_ID_DEVICE_NAME],
    right: [COLUMN_ID_ACTION],
  },
  id: 'acknowledged-alarms-table',
  initialSortingState: [{ id: COLUMN_ID_DATE, desc: true }],
  scrollContainerHeight: 'h-[75vh]',
  stickyHeader: true,
  tableDensity: TableDensity.WIDE,
};

export const AcknowledgedAlarmsTable: React.FC<AlarmsTableProps> = ({
  alarms,
  containerRef,
  isLoading = false,
  onOpenTipsToResolve,
  isProcessing = false,
}) => {
  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });
  const { isDisabled, tooltipText } = useActionPermission({ privilege: PRIVILEGES.resetAlarms });
  const { organizationId, agentId } = useCurrentFacility();

  /*
   * Pull the Cached Sorting State from Local Storage. This allows us to initialize
   * the table with the correct sorting and column size state.
   */
  const [cachedTableState] = usePersistentState<DataTableCache>(localStorage, options.id);

  /**
   * For this particular table, pinning is dependent on our status as Mobile.gs
   */
  const columnPinningState: ColumnPinningState = useMemo(() => {
    return isMobile
      ? { left: options.columnPinningState.left, right: [] }
      : options.columnPinningState;
  }, [isMobile]);

  // Memoize the column set, and make updated specific to this Table.
  const acknowledgedColumns = useMemo<
    AcknowledgedDataTableColumnDef<AlarmTableData, AlarmTableData>[]
  >(() => {
    return [
      {
        ...SeverityColumnDef,
        headerText: isMobile ? '' : SeverityColumnDef.headerText,
      },
      DeviceNameColumnDef,
      DeviceTypeColumnDef,
      IssueColumnDef,
      DateColumnDef,
      StatusColumnDef,
      ActiveForColumnDef,
      AcknowledgedAtColumnDef,
      AcknowledgedByColumnDef,
      ResolvedAtColumnDef,
      {
        ...ActionColumnDef,
        meta: {
          agentId: agentId,
          disabled: !organizationId || !agentId || isDisabled,
          tooltipText: !organizationId || !agentId || isDisabled ? tooltipText : undefined,
          isLoading: isProcessing,
          organizationId: organizationId,
        },
      },
    ];
  }, [isMobile, organizationId, agentId, isProcessing, isDisabled, tooltipText]);

  // Memoize the data for the table.
  const data = useMemo(
    () =>
      (alarms || []).map((alarm: AlarmActivation): AlarmTableData => {
        return {
          action: 'Clear',
          activationId: alarm.activationId,
          activeFor: getElapsedTime(alarm.activatedAt, alarm.resolvedAt),
          acknowledgedAt: alarm.acknowledgedAt,
          acknowledgedBy: alarm.acknowledgedBy
            ? `${alarm.acknowledgedBy.firstName} ${alarm.acknowledgedBy.lastName}`
            : undefined,
          current: getSnapshotString(alarm.controlPoints?.at(0)) || MISSING_VALUE,
          date: alarm.activatedAt,
          deviceName: alarm.device?.name || MISSING_VALUE,
          deviceType: alarm.device?.type || MISSING_VALUE,
          issue: alarm.message,
          resolvedAt: alarm.resolvedAt,
          severity: alarm.severity,
          status: getStatus(alarm),
        };
      }),
    [alarms]
  );

  // Helper hook to create the table. This helps separate the logic of the table with the display
  // allowing us access to the table state and settings without have to pass it back up the
  // component tree.
  const table = useDataTable<AlarmTableData, AlarmTableData>(
    options.id,
    acknowledgedColumns,
    data,
    {
      ...options,
      columnPinningState: columnPinningState,
      initialSortingState: cachedTableState?.sorting || options.initialSortingState,
      widthContainerRef: containerRef as React.RefObject<HTMLDivElement>,
      virtualizeSettings: {
        isVirtualized: true,
        estimateSize: 48,
        overscan: 10,
      },
      experimentalDragScroll: true,
    },
    {
      onOpenTipsToResolve,
    }
  );

  return (
    // Tooltip island for this legacy useDataTable grid (rationale on the
    // constant): its Acknowledge/Clear TooltipButtons use a bare Tooltip with
    // no pinned delay, so without the island they would be instant per-cell.
    <TableTooltipIsland>
      <DataTable
        headerCells={table.headerCells}
        isLoading={isLoading}
        rows={table.rows}
        settings={table.settings}
        tableId={table.dataTableId}
      />
    </TableTooltipIsland>
  );
};
